import {
  normalizePedaCategory,
  PEDA_CATEGORY_KEYS,
  PEDA_CATEGORY_LABELS,
  PEDA_CATEGORY_SHORT_LABELS,
  PEDA_CATEGORY_TILE_KEYS,
  type PedaCategoryKey,
} from './peda-categories';
import { getMissionIdForScene } from './mission-progress';
import { findChapterContainingScene, getChapterSceneIds } from './scene-flow';
import type { Mission, Scenario, Scene } from '../types';

export type PedaCategoryCounts = Record<PedaCategoryKey, number>;

export interface PedaCampaignStore {
  byMission: Record<string, Partial<PedaCategoryCounts>>;
  lastRankRatio?: number;
}

export interface PedaRankResult {
  label: string;
  percent: number;
}

export interface PedaCategoryScoreRow {
  key: PedaCategoryKey;
  label: string;
  shortLabel: string;
  earned: number;
  max: number;
}

export interface PedaEndStats {
  rank: PedaRankResult;
  progressDelta: string | null;
  categories: PedaCategoryScoreRow[];
  earnedTotal: number;
  possibleTotal: number;
}

export const CAMPAIGN_KEY_PREFIX = 'vn_peda_campaign_';

export function emptyPedaCategoryCounts(): PedaCategoryCounts {
  return {
    hameconnage: 0,
    manipulation: 0,
    incidents: 0,
    donnees: 0,
    appareils: 0,
    acces: 0,
  };
}

export function pedaCategoryVariableKey(category: PedaCategoryKey): string {
  return `peda_cat_${category}`;
}

export function pedaCategoryChapterStartKey(category: PedaCategoryKey): string {
  return `peda_cat_${category}_chapter_start`;
}

export function readLiveCategoryCounts(variables: Record<string, number>): PedaCategoryCounts {
  const counts = emptyPedaCategoryCounts();
  for (const key of PEDA_CATEGORY_KEYS) {
    counts[key] = variables[pedaCategoryVariableKey(key)] ?? 0;
  }
  return counts;
}

export function resetLiveCategoryCounts(variables: Record<string, number>): Record<string, number> {
  const next = { ...variables };
  for (const key of PEDA_CATEGORY_KEYS) {
    next[pedaCategoryVariableKey(key)] = 0;
  }
  return next;
}

export function snapshotChapterCategoryCounts(
  variables: Record<string, number>,
): Record<string, number> {
  const next = { ...variables };
  for (const key of PEDA_CATEGORY_KEYS) {
    next[pedaCategoryChapterStartKey(key)] = variables[pedaCategoryVariableKey(key)] ?? 0;
  }
  return next;
}

export function readChapterStartCategoryCounts(
  variables: Record<string, number>,
): PedaCategoryCounts {
  const counts = emptyPedaCategoryCounts();
  for (const key of PEDA_CATEGORY_KEYS) {
    counts[key] = variables[pedaCategoryChapterStartKey(key)] ?? 0;
  }
  return counts;
}

export function chapterEarnedCategoryCounts(
  liveCounts: PedaCategoryCounts,
  chapterStartCounts: PedaCategoryCounts,
): PedaCategoryCounts {
  const earned = emptyPedaCategoryCounts();
  for (const key of PEDA_CATEGORY_KEYS) {
    earned[key] = Math.max(0, (liveCounts[key] ?? 0) - (chapterStartCounts[key] ?? 0));
  }
  return earned;
}

export function sumPedaCategoryCounts(counts: Partial<PedaCategoryCounts>): number {
  return PEDA_CATEGORY_KEYS.reduce((sum, key) => sum + (counts[key] ?? 0), 0);
}

function addShieldToCounts(
  counts: PedaCategoryCounts,
  category: unknown,
  amount: number,
): void {
  if (amount <= 0) return;
  const key = normalizePedaCategory(category);
  if (!key) return;
  counts[key] += amount;
}

function collectFromScene(
  scene: Scene,
  counts: PedaCategoryCounts,
  visitBranchScenes: (scenes: Scene[]) => void,
): void {
  if (scene.type === 'choice') {
    for (const option of scene.options) {
      addShieldToCounts(counts, option.pedaCategory, option.pedaShields ?? 0);
      if (option.scenes?.length) {
        visitBranchScenes(option.scenes);
      }
    }
    return;
  }

  if (scene.type === 'phoneMail') {
    for (const action of scene.triageActions ?? []) {
      addShieldToCounts(counts, action.pedaCategory, action.pedaShields ?? 0);
    }
    for (const mail of scene.mails ?? []) {
      for (const action of mail.triageActions) {
        addShieldToCounts(counts, action.pedaCategory, action.pedaShields ?? 0);
      }
    }
    return;
  }

  if (scene.type === 'observation') {
    for (const element of scene.elements ?? []) {
      if (element.type === 'object') {
        addShieldToCounts(counts, element.pedaCategory, element.pedaShields ?? 0);
      }
    }
    return;
  }

  if (scene.type === 'clickableScreen') {
    let maxShields = 0;
    let maxCategory: unknown;
    for (const zone of scene.zones ?? []) {
      const shields = zone.pedaShields ?? 0;
      if (shields > maxShields) {
        maxShields = shields;
        maxCategory = zone.pedaCategory;
      }
    }
    addShieldToCounts(counts, maxCategory, maxShields);
    return;
  }

  if (scene.type === 'situationSortGame') {
    for (const situation of scene.situations) {
      addShieldToCounts(counts, situation.pedaCategory, situation.pedaShields ?? 0);
    }
  }
}

function buildMissionOrderMap(missions: Mission[] | undefined): Map<string, number> {
  return new Map((missions ?? []).map((mission) => [mission.id, mission.order]));
}

function isMissionIncluded(
  missionId: string | undefined,
  missionOrderById: Map<string, number>,
  upToMissionOrder: number,
): boolean {
  if (!missionId) return upToMissionOrder >= 1;
  const order = missionOrderById.get(missionId);
  if (order === undefined) return false;
  return order <= upToMissionOrder;
}

function collectFromSceneList(
  sceneList: Scene[],
  scenario: Scenario,
  upToMissionOrder: number,
  counts: PedaCategoryCounts,
  missionOrderById: Map<string, number>,
  inheritedMissionId?: string,
): void {
  const visitBranchScenes = (branchScenes: Scene[]) => {
    collectFromSceneList(
      branchScenes,
      scenario,
      upToMissionOrder,
      counts,
      missionOrderById,
      inheritedMissionId,
    );
  };

  for (const scene of sceneList) {
    const missionId = inheritedMissionId
      ?? getMissionIdForScene(scenario.scenes, scenario.metadata.chapters, scene.id);
    if (!isMissionIncluded(missionId, missionOrderById, upToMissionOrder)) continue;
    collectFromScene(scene, counts, visitBranchScenes);
  }
}

function collectFromScenesUnfiltered(
  sceneList: Scene[],
  counts: PedaCategoryCounts,
): void {
  const visitBranchScenes = (branchScenes: Scene[]) => {
    collectFromScenesUnfiltered(branchScenes, counts);
  };
  for (const scene of sceneList) {
    collectFromScene(scene, counts, visitBranchScenes);
  }
}

/** Sum of earnable pedaShields per category for missions with order <= upToMissionOrder. */
export function collectPedaShieldsByCategory(
  scenario: Scenario,
  upToMissionOrder: number,
): PedaCategoryCounts {
  const counts = emptyPedaCategoryCounts();
  const missionOrderById = buildMissionOrderMap(scenario.metadata.missions);
  collectFromSceneList(scenario.scenes, scenario, upToMissionOrder, counts, missionOrderById);
  return counts;
}

/** Earnable pedaShields in the chapter that contains `sceneId` (including nested branches). */
export function collectPedaShieldsForChapter(
  scenario: Scenario,
  sceneId: string,
): PedaCategoryCounts {
  const counts = emptyPedaCategoryCounts();
  const chapter = findChapterContainingScene(scenario, sceneId);
  if (!chapter) return counts;

  const chapterSceneIds = new Set(getChapterSceneIds(scenario, chapter.startScene));
  const chapterScenes = scenario.scenes.filter((scene) => chapterSceneIds.has(scene.id));
  collectFromScenesUnfiltered(chapterScenes, counts);
  return counts;
}

export function resolvePedaRank(earned: number, possible: number): PedaRankResult {
  const percent = possible > 0 ? Math.round((earned / possible) * 100) : 0;
  let label: string;
  if (percent < 40) {
    label = 'Bonne volonté, réflexes à construire';
  } else if (percent < 60) {
    label = "L'œil qui s'ouvre";
  } else if (percent < 75) {
    label = 'Vigie';
  } else if (percent < 90) {
    label = 'Sentinelle';
  } else {
    label = 'Gardien du Heaume';
  }
  return { label, percent };
}

export type PedaTileStatus = 'empty' | 'weak' | 'mid' | 'strong';

export function resolvePedaTileStatus(earned: number, max: number): {
  status: PedaTileStatus;
  caption: string;
} {
  if (max <= 0) return { status: 'empty', caption: 'PAS ENCORE CROISÉ' };
  const percent = (earned / max) * 100;
  if (percent < 40) return { status: 'weak', caption: 'À REVOIR' };
  if (percent < 70) return { status: 'mid', caption: 'ÇA VIENT' };
  return { status: 'strong', caption: 'SOLIDE' };
}

export function resolvePedaProgressDelta(
  currentPercent: number,
  lastRankRatio?: number,
): string | null {
  if (lastRankRatio === undefined) return null;
  const previous = Math.round(lastRankRatio);
  const delta = currentPercent - previous;
  if (delta === 0) return 'Stable depuis la mission précédente';
  if (delta > 0) return `+${delta} pts depuis la mission précédente`;
  return `${delta} pts depuis la mission précédente`;
}

export function loadPedaCampaignStore(scenarioId: string): PedaCampaignStore {
  try {
    const raw = localStorage.getItem(`${CAMPAIGN_KEY_PREFIX}${scenarioId}`);
    if (!raw) return { byMission: {} };
    const parsed = JSON.parse(raw) as PedaCampaignStore;
    return {
      byMission: parsed.byMission ?? {},
      lastRankRatio: typeof parsed.lastRankRatio === 'number' ? parsed.lastRankRatio : undefined,
    };
  } catch {
    return { byMission: {} };
  }
}

export function savePedaCampaignStore(scenarioId: string, store: PedaCampaignStore): void {
  localStorage.setItem(`${CAMPAIGN_KEY_PREFIX}${scenarioId}`, JSON.stringify(store));
}

export function mergeCampaignEarnedCounts(
  store: PedaCampaignStore,
  missions: Mission[],
  currentMissionId: string | undefined,
  currentMissionOrder: number,
  liveCounts: PedaCategoryCounts,
): PedaCategoryCounts {
  const merged = emptyPedaCategoryCounts();

  for (const mission of missions) {
    if (mission.order >= currentMissionOrder) continue;
    const stored = store.byMission[mission.id];
    if (!stored) continue;
    for (const key of PEDA_CATEGORY_KEYS) {
      merged[key] += stored[key] ?? 0;
    }
  }

  if (currentMissionId) {
    const currentMission = missions.find((mission) => mission.id === currentMissionId);
    if (currentMission && currentMission.order === currentMissionOrder) {
      for (const key of PEDA_CATEGORY_KEYS) {
        merged[key] += liveCounts[key] ?? 0;
      }
    }
  }

  return merged;
}

export function buildPedaEndStats(options: {
  scenario: Scenario;
  currentSceneId: string;
  liveCounts: PedaCategoryCounts;
  chapterStartCounts?: PedaCategoryCounts;
}): PedaEndStats | null {
  const { scenario, currentSceneId, liveCounts, chapterStartCounts } = options;
  if (!currentSceneId) return null;

  const possible = collectPedaShieldsForChapter(scenario, currentSceneId);
  const earned = chapterEarnedCategoryCounts(
    liveCounts,
    chapterStartCounts ?? emptyPedaCategoryCounts(),
  );

  const earnedTotal = sumPedaCategoryCounts(earned);
  const possibleTotal = sumPedaCategoryCounts(possible);
  const rank = resolvePedaRank(earnedTotal, possibleTotal);

  const categories = PEDA_CATEGORY_TILE_KEYS.map((key) => ({
    key,
    label: PEDA_CATEGORY_LABELS[key],
    shortLabel: PEDA_CATEGORY_SHORT_LABELS[key],
    earned: earned[key],
    max: possible[key],
  }));

  return { rank, progressDelta: null, categories, earnedTotal, possibleTotal };
}

export function flushLiveMissionToCampaign(
  scenarioId: string,
  missionId: string,
  liveCounts: PedaCategoryCounts,
  snapshotRankRatio?: number,
): PedaCampaignStore {
  const store = loadPedaCampaignStore(scenarioId);
  store.byMission[missionId] = { ...liveCounts };
  if (snapshotRankRatio !== undefined) {
    store.lastRankRatio = snapshotRankRatio;
  }
  savePedaCampaignStore(scenarioId, store);
  return store;
}

export function applyCategoryShieldGain(
  variables: Record<string, number>,
  category: PedaCategoryKey | null | undefined,
  amount: number,
): Record<string, number> {
  if (!category || amount <= 0) return variables;
  const key = pedaCategoryVariableKey(category);
  return {
    ...variables,
    [key]: (variables[key] ?? 0) + amount,
  };
}

export function applyCategoryShieldRevoke(
  variables: Record<string, number>,
  byCategory: Partial<PedaCategoryCounts>,
): Record<string, number> {
  const next = { ...variables };
  for (const category of PEDA_CATEGORY_KEYS) {
    const revoke = byCategory[category] ?? 0;
    if (revoke <= 0) continue;
    const key = pedaCategoryVariableKey(category);
    next[key] = Math.max(0, (next[key] ?? 0) - revoke);
  }
  return next;
}
